// External libraries
import { useMemo, useState } from 'react';
import * as React from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wallet,
  Filter,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// Internal - Components
import { StatCard } from '../../components/primitives/StatCard';
import { SelectedCompaniesIndicator } from '../../components/primitives/SelectedCompaniesIndicator';
import { EmptyState } from '../../components/primitives/EmptyState';
import { Container, Stack } from '../../components/primitives';
import { PageHeader } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { DashboardSkeleton } from './components/DashboardSkeleton';
import { BudgetWidget } from './components/BudgetWidget';
import { BudgetAlerts } from './components/BudgetAlerts';
import { CategoryBudgetStatus } from './components/CategoryBudgetStatus';
import { QuickActionsMenu } from './components/QuickActionsMenu';
import { RecentTransactions } from './components/RecentTransactions';
import { LeverageCapacityWidget } from './components/LeverageCapacityWidget';

// Internal - Hooks
import { useCashFlowEntries } from '../../hooks/useCashFlowEntries';
import { useCompanyContext } from '../../contexts/CompanyContext';
import { useFinancing } from '../../hooks/useFinancing';
import { useAuth } from '../../hooks/useAuth';
import { exchangeRateService } from '../../lib/services/exchangeRateService';

// Internal - Utils
import { formatCurrency, formatCurrencySmart, sumWithConversion } from '../../lib/utils/format';
import { useCompanyCurrency } from '../../hooks/useCompanyCurrency';
import { DEFAULT_CURRENCY } from '../../lib/constants';
import type { Currency } from '../../types/currency';

interface DashboardPageProps {
  userId: string;
  onNavigateToTransactionForm?: (transactionId?: string) => void;
  onNavigate?: (path: string) => void;
}

export function DashboardPage({ 
  userId,
  onNavigateToTransactionForm,
  onNavigate,
}: DashboardPageProps) {
  const { selectedCompanyIds, hasMultipleSelected, selectedCompanies } = useCompanyContext();
  
  console.log('[DashboardPage] 🏢 Company Context:', {
    selectedCompanyIds,
    selectedCompaniesCount: selectedCompanies.length,
    selectedCompaniesNames: selectedCompanies.map(c => c.name),
  });
  
  const { entries, loading } = useCashFlowEntries(selectedCompanyIds, userId);
  
  // Financing data
  const { totals: financingTotals, financingAccounts, loading: financingLoading } = useFinancing({
    companyIds: selectedCompanyIds,
    userId,
  });
  
  // Payment Status Filter
  const [statusFilter, setStatusFilter] = useState<'all' | 'confirmado' | 'previsão' | 'pendente'>('all');
  
  // Get base currency from first selected company (or default to BRL)
  const baseCurrency: Currency = selectedCompanies[0]?.default_currency || DEFAULT_CURRENCY;
  
  // Fetch exchange rates for currency conversion
  const [rates, setRates] = React.useState<Record<string, number> | null>(null);
  const [ratesLoading, setRatesLoading] = React.useState(true);
  const { user } = useAuth();
  
  React.useEffect(() => {
    async function loadRates() {
      setRatesLoading(true);
      
      if (!user?.id) {
        setRates(null);
        setRatesLoading(false);
        return;
      }
      
      const apiKey = user.user_metadata?.exchangerate_api_key;
      if (!apiKey) {
        setRates(null);
        setRatesLoading(false);
        return;
      }
      
      try {
        const ratesForBase = await exchangeRateService.getRatesForBase(user.id, baseCurrency);
        const ratesMap: Record<string, number> = {};
        ratesForBase.forEach(r => {
          ratesMap[r.target_currency] = r.rate;
        });
        console.log('[DashboardPage] ✅ Rates loaded:', { baseCurrency, count: Object.keys(ratesMap).length, rates: ratesMap });
        setRates(ratesMap);
      } catch (error) {
        console.error('[DashboardPage] Error loading rates:', error);
        setRates(null);
      } finally {
        setRatesLoading(false);
      }
    }
    
    loadRates();
  }, [user?.id, baseCurrency]);

  const stats = useMemo(() => {
    // Don't calculate until rates are loaded (prevents calculation with stale/null rates)
    if (ratesLoading && hasMultipleSelected) {
      console.log('[DashboardPage] ⏳ Waiting for rates to load before calculating stats...');
      return {
        income: 0,
        expenses: 0,
        balance: 0,
        incomeTrend: 0,
        expensesTrend: 0,
      };
    }
    
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const currentMonthEntries = entries.filter((entry) => {
      // Use same date logic as display: payment_date > due_date > entry_date
      const relevantDate = new Date(entry.payment_date || entry.due_date || entry.entry_date || entry.created_at);
      const matchesDate = relevantDate.getMonth() === currentMonth &&
        relevantDate.getFullYear() === currentYear;
      const matchesStatus = statusFilter === 'all' || entry.payment_status === statusFilter;
      
      return matchesDate && matchesStatus;
    });

    const incomeEntries = currentMonthEntries.filter((e) => e.entry_type === 'income');
    const expenseEntries = currentMonthEntries.filter((e) => e.entry_type === 'expense');
    
    // Get currency breakdown from entries
    const currencyBreakdown = entries.reduce((acc, e) => {
      const curr = e.currency || 'BRL';
      acc[curr] = (acc[curr] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    console.log('[DashboardPage] 📊 Calculating stats:', { 
      ratesLoading, 
      hasRates: !!rates, 
      ratesCount: rates ? Object.keys(rates).length : 0,
      totalEntries: entries.length,
      currentMonthEntries: currentMonthEntries.length,
      incomeEntries: incomeEntries.length,
      expenseEntries: expenseEntries.length,
      baseCurrency,
      hasMultipleSelected,
      selectedCompaniesCount: selectedCompanies.length,
      companyCurrencies: selectedCompanies.map(c => c.default_currency),
      entriesCurrencyBreakdown: currencyBreakdown,
      incomeEntriesSample: incomeEntries.slice(0, 3).map(e => ({ amount: e.amount, currency: e.currency })),
      expenseEntriesSample: expenseEntries.slice(0, 3).map(e => ({ amount: e.amount, currency: e.currency })),
    });

    const income = sumWithConversion(incomeEntries, baseCurrency, rates);
    const expenses = sumWithConversion(expenseEntries, baseCurrency, rates);
    
    console.log('[DashboardPage] 💰 Results:', { income, expenses, balance: income - expenses });

    const balance = income - expenses;

    // Calculate trend (comparing to last month)
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    const lastMonthEntries = entries.filter((entry) => {
      // Use same date logic as display: payment_date > due_date > entry_date
      const relevantDate = new Date(entry.payment_date || entry.due_date || entry.entry_date || entry.created_at);
      const matchesDate = relevantDate.getMonth() === lastMonth &&
        relevantDate.getFullYear() === lastMonthYear;
      const matchesStatus = statusFilter === 'all' || entry.payment_status === statusFilter;
      
      return matchesDate && matchesStatus;
    });

    const lastMonthIncome = sumWithConversion(
      lastMonthEntries.filter((e) => e.entry_type === 'income'),
      baseCurrency,
      rates
    );

    const lastMonthExpenses = sumWithConversion(
      lastMonthEntries.filter((e) => e.entry_type === 'expense'),
      baseCurrency,
      rates
    );

    const incomeTrend =
      lastMonthIncome > 0
        ? ((income - lastMonthIncome) / lastMonthIncome) * 100
        : 0;
    const expensesTrend =
      lastMonthExpenses > 0
        ? ((expenses - lastMonthExpenses) / lastMonthExpenses) * 100
        : 0;

    return {
      income,
      expenses,
      balance,
      incomeTrend,
      expensesTrend,
    };
  }, [entries, rates, baseCurrency, statusFilter, ratesLoading, hasMultipleSelected]);

  const monthlyData = useMemo(() => {
    const now = new Date();
    const months = [];

    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const month = date.getMonth();
      const year = date.getFullYear();

      const monthEntries = entries.filter((entry) => {
        // Use same date logic as display: payment_date > due_date > entry_date
        const relevantDate = new Date(entry.payment_date || entry.due_date || entry.entry_date || entry.created_at);
        const matchesDate = relevantDate.getMonth() === month &&
          relevantDate.getFullYear() === year;
        const matchesStatus = statusFilter === 'all' || entry.payment_status === statusFilter;
        
        return matchesDate && matchesStatus;
      });

      const income = sumWithConversion(
        monthEntries.filter((e) => e.entry_type === 'income'),
        baseCurrency,
        rates
      );

      const expenses = sumWithConversion(
        monthEntries.filter((e) => e.entry_type === 'expense'),
        baseCurrency,
        rates
      );

      months.push({
        month: date.toLocaleDateString('pt-BR', { month: 'short' }),
        receitas: income,
        despesas: expenses,
      });
    }

    return months;
  }, [entries, rates, baseCurrency, statusFilter, ratesLoading]);

  const categoryData = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const categoryTotals = new Map<string, number>();

    entries
      .filter((entry) => {
        // Use same date logic as display: payment_date > due_date > entry_date
        const relevantDate = new Date(entry.payment_date || entry.due_date || entry.entry_date || entry.created_at);
        const matchesDate = relevantDate.getMonth() === currentMonth &&
          relevantDate.getFullYear() === currentYear;
        const matchesStatus = statusFilter === 'all' || entry.payment_status === statusFilter;
        const matchesType = entry.entry_type === 'expense';
        
        return matchesDate && matchesStatus && matchesType;
      })
      .forEach((entry) => {
        const category = entry.main_category;
        const converted = sumWithConversion([entry], baseCurrency, rates);
        categoryTotals.set(
          category,
          (categoryTotals.get(category) || 0) + converted
        );
      });

    return Array.from(categoryTotals.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [entries, rates, baseCurrency, statusFilter, ratesLoading]);

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
  ];

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (entries.length === 0) {
    return (
      <>
        <Container size="large" className="py-6 md:py-8">
          <EmptyState
            icon={DollarSign}
            title="Nenhum lançamento encontrado"
            description="Comece adicionando seus primeiros lançamentos financeiros para visualizar o dashboard."
          />
        </Container>
        <QuickActionsMenu 
          onNavigateToNewTransaction={(type) => onNavigateToTransactionForm?.()}
          onNavigateToBudget={() => onNavigate?.('/budget')}
          onNavigateToTransactions={() => onNavigate?.('/entries')}
        />
      </>
    );
  }

  return (
    <>
      <Container size="large" className="py-6 md:py-8">
        <Stack spacing="large">
          {/* Header */}
          <PageHeader
            icon={DollarSign}
            title="Dashboard Financeiro"
            description="Visão geral e controle do mês atual"
          />

          {/* Selected Companies Indicator */}
          <SelectedCompaniesIndicator />

          {/* Payment Status Filter */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Filtrar por status:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={statusFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setStatusFilter('all')}
                  >
                    Todos
                  </Button>
                  <Button
                    size="sm"
                    variant={statusFilter === 'confirmado' ? 'default' : 'outline'}
                    onClick={() => setStatusFilter('confirmado')}
                  >
                    Confirmado
                  </Button>
                  <Button
                    size="sm"
                    variant={statusFilter === 'previsão' ? 'default' : 'outline'}
                    onClick={() => setStatusFilter('previsão')}
                  >
                    Previsão
                  </Button>
                  <Button
                    size="sm"
                    variant={statusFilter === 'pendente' ? 'default' : 'outline'}
                    onClick={() => setStatusFilter('pendente')}
                  >
                    Pendente
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ============================================ */}
          {/* SECTION 1: Stats Cards (Most Important) */}
          {/* ============================================ */}
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
            <StatCard
              title="Receitas"
              value={formatCurrencySmart(stats.income, baseCurrency)}
              fullValue={formatCurrency(stats.income, baseCurrency)}
              icon={TrendingUp}
              trend={
                stats.incomeTrend
                  ? {
                      value: Math.abs(stats.incomeTrend),
                      isPositive: stats.incomeTrend > 0,
                    }
                  : undefined
              }
              valueClassName="text-success-600 dark:text-success-400"
            />
            <StatCard
              title="Despesas"
              value={formatCurrencySmart(stats.expenses, baseCurrency)}
              fullValue={formatCurrency(stats.expenses, baseCurrency)}
              icon={TrendingDown}
              trend={
                stats.expensesTrend
                  ? {
                      value: Math.abs(stats.expensesTrend),
                      isPositive: stats.expensesTrend < 0,
                    }
                  : undefined
              }
              valueClassName="text-danger-600 dark:text-danger-400"
            />
            <StatCard
              title="Saldo"
              value={formatCurrencySmart(stats.balance, baseCurrency)}
              fullValue={formatCurrency(stats.balance, baseCurrency)}
              icon={Wallet}
              valueClassName={
                stats.balance > 0
                  ? 'text-success-600 dark:text-success-400'
                  : stats.balance < 0
                  ? 'text-danger-600 dark:text-danger-400'
                  : ''
              }
            />
            <StatCard
              title="Lançamentos"
              value={entries.length}
              icon={DollarSign}
            />
          </div>

          {/* ============================================ */}
          {/* SECTION 2: Budget Overview (Full Width) */}
          {/* ============================================ */}
          <BudgetWidget 
            companyIds={selectedCompanyIds} 
            userId={userId}
            onNavigateToBudget={() => onNavigate?.('/budget')}
          />

          {/* ============================================ */}
          {/* SECTION 2.5: Leverage Capacity */}
          {/* ============================================ */}
          <LeverageCapacityWidget
            totalLimit={financingTotals.totalLimit}
            totalUsed={financingTotals.totalUsed}
            totalAvailable={financingTotals.totalAvailable}
            accountsCount={financingAccounts.length}
            loading={financingLoading}
            onViewDetails={() => onNavigate?.('/reports?report=leverage-capacity')}
          />

          {/* ============================================ */}
          {/* SECTION 3: Recent Transactions (Full Width) */}
          {/* ============================================ */}
          <RecentTransactions
            companyIds={selectedCompanyIds}
            userId={userId}
            limit={5}
            onNavigateToNewTransaction={() => onNavigateToTransactionForm?.()}
            onNavigateToTransactions={() => onNavigate?.('/entries')}
          />

          {/* ============================================ */}
          {/* SECTION 4: Budget Alerts + Category Status */}
          {/* ============================================ */}
          <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
            <BudgetAlerts 
              companyIds={selectedCompanyIds} 
              userId={userId}
              onNavigateToBudget={() => onNavigate?.('/budget')}
            />
            <CategoryBudgetStatus 
              companyIds={selectedCompanyIds} 
              userId={userId}
              limit={5}
              onNavigateToBudget={() => onNavigate?.('/budget')}
            />
          </div>

          {/* ============================================ */}
          {/* SECTION 5: Charts (Analytics) */}
          {/* ============================================ */}
          <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
            {/* Monthly Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Receitas vs Despesas (6 meses)</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="month" 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value, companyCurrency)}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="receitas"
                      fill="hsl(var(--success-600))"
                      name="Receitas"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="despesas"
                      fill="hsl(var(--danger-600))"
                      name="Despesas"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Category Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Categorias de Despesas</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => formatCurrency(value, companyCurrency)}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                    Nenhuma despesa neste mês
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </Stack>
      </Container>

      {/* Floating Action Button */}
      <QuickActionsMenu 
        onNavigateToNewTransaction={(type) => onNavigateToTransactionForm?.()}
        onNavigateToBudget={() => onNavigate?.('/budget')}
        onNavigateToTransactions={() => onNavigate?.('/entries')}
      />
    </>
  );
}
